<template>
	<div>
		<v-row dense data-tour="kpi">
			<v-col v-for="t in tiles" :key="t.label" cols="6" md="4" lg="3">
				<v-card outlined class="co-tile">
					<div class="co-label">
						{{ t.label }}
						<v-tooltip v-if="t.hint" bottom max-width="320">
							<template v-slot:activator="{ on }">
								<v-icon x-small color="grey" v-on="on">mdi-information-outline</v-icon>
							</template>
							<span>{{ t.hint }}</span>
						</v-tooltip>
					</div>
					<div class="co-value">{{ t.value }}</div>
					<div v-if="t.delta !== undefined" class="co-delta" :class="`${deltaColor(t.delta, t.inverse)}--text`">{{ formatDelta(t.delta) }} rispetto a {{ previousLabel }}</div>
					<div v-else-if="t.sub" class="co-sub">{{ t.sub }}</div>
				</v-card>
			</v-col>
		</v-row>

		<v-row dense class="mt-1">
			<v-col cols="12" lg="6">
				<SectionCard title="Nuovi e di ritorno nel periodo" icon="mdi-account-switch-outline" subtitle="spesa IVA inclusa">
					<div v-for="b in split" :key="b.label" class="co-split">
						<div class="d-flex">
							<span>{{ b.label }}</span>
							<v-spacer />
							<span class="font-weight-medium">{{ formatNumber(b.customers) }} clienti · {{ formatEurCents(b.spend) }}</span>
						</div>
						<v-progress-linear :value="b.share" :color="b.color" height="8" rounded class="mt-1" />
						<div class="co-sub">{{ formatPct(b.share) }} della spesa · {{ formatNumber(b.orders) }} ordini</div>
					</div>
					<div class="co-sub mt-2">
						Nuovo = primo ordine di sempre dentro il periodo. Un cliente che compra sul sito e su Amazon con email diverse conta due volte. Gli ordini senza email né username ({{
							formatPct(report.unidentifiedShare)
						}}) entrano nelle vendite ma non nei clienti.
					</div>
				</SectionCard>
			</v-col>
			<v-col cols="12" lg="6">
				<SectionCard title="Prodotti d'ingresso" icon="mdi-door-open" subtitle="nei primi ordini, di sempre" no-padding data-tour="entry">
					<v-simple-table dense>
						<thead>
							<tr>
								<th>Prodotto</th>
								<th class="text-right">Primi ordini</th>
								<th class="text-right">Sono tornati</th>
							</tr>
						</thead>
						<tbody>
							<tr v-for="p in entryProducts.slice(0, 10)" :key="p.id">
								<td class="co-title">{{ p.title || p.id }}</td>
								<td class="text-right">{{ formatNumber(p.customers) }}</td>
								<td class="text-right">{{ formatPct(p.returnRate) }}</td>
							</tr>
							<tr v-if="!entryProducts.length">
								<td colspan="3" class="text-center grey--text py-4">Nessun dato</td>
							</tr>
						</tbody>
					</v-simple-table>
					<div class="co-sub pa-3">I prodotti con cui i clienti iniziano e quanti di loro hanno poi comprato ancora: quelli con la quota più alta portano clienti che restano.</div>
				</SectionCard>
			</v-col>
		</v-row>
	</div>
</template>

<script>
import SectionCard from '@/components/core/SectionCard.vue';
import { formatEurCents, formatNumber, formatPct, formatDelta, deltaColor } from '../format';

const change = (a, b) => {
	if (b === undefined || b === null) return undefined;
	if (!b) return a ? null : 0;
	return Math.round(((a - b) / Math.abs(b)) * 1000) / 10;
};

export default {
	name: 'CustomersOverview',
	components: { SectionCard },
	props: {
		report: { type: Object, required: true },
		previous: { type: Object, default: null },
		lifetime: { type: Object, default: null },
		entryProducts: { type: Array, default: () => [] },
		previousLabel: { type: String, default: '' },
	},
	computed: {
		tiles() {
			const r = this.report;
			const p = this.previous || {};
			const life = this.lifetime || {};
			const cmp = key => (this.previous ? change(r[key], p[key]) : undefined);
			return [
				{ label: 'Clienti attivi', value: formatNumber(r.customers), delta: cmp('customers'), hint: 'Clienti con almeno un ordine nel periodo.' },
				{ label: 'Nuovi clienti', value: formatNumber(r.newCustomers), delta: cmp('newCustomers') },
				{
					label: 'Clienti di ritorno',
					value: `${formatNumber(r.returning)} · ${formatPct(r.returningRate)}`,
					delta: cmp('returning'),
					hint: 'Hanno comprato nel periodo e avevano già comprato prima.',
				},
				{ label: 'Valore medio dell’ordine', value: formatEurCents(r.aov), delta: cmp('aov') },
				{ label: 'Ordini per cliente', value: String(r.ordersPerCustomer).replace('.', ','), delta: cmp('ordersPerCustomer') },
				{
					label: 'Valore medio del cliente',
					value: formatEurCents(life.avgSpend),
					sub: `in tutta la storia · ${String(life.avgOrders || 0).replace('.', ',')} ordini a testa`,
					hint: 'Spesa totale di un cliente da quando compra, in media (lifetime value).',
				},
				{ label: 'Clienti che ricomprano', value: formatPct(life.repeatRate), sub: `${formatNumber(life.oneTime)} hanno comprato una volta sola` },
				{ label: 'Tempo al secondo acquisto', value: life.daysToSecond === null || life.daysToSecond === undefined ? 'n.d.' : `${life.daysToSecond} giorni`, sub: 'in media, per chi è tornato' },
			];
		},
		split() {
			const r = this.report;
			const total = r.spend || 0;
			return [
				{ label: 'Nuovi', customers: r.newCustomers, orders: r.newOrders, spend: r.newSpend, share: total ? Math.round((r.newSpend / total) * 1000) / 10 : 0, color: 'primary' },
				{
					label: 'Di ritorno',
					customers: r.returning,
					orders: r.returningOrders,
					spend: r.returningSpend,
					share: total ? Math.round((r.returningSpend / total) * 1000) / 10 : 0,
					color: 'green darken-1',
				},
			];
		},
	},
	methods: { formatEurCents, formatNumber, formatPct, formatDelta, deltaColor },
};
</script>

<style scoped>
.co-tile {
	border-radius: 10px !important;
	padding: 12px 14px;
	height: 100%;
}
.co-label {
	font-size: 12px;
	color: #5f6b7a;
}
.co-value {
	font-size: 20px;
	font-weight: 600;
	margin-top: 2px;
}
.co-delta,
.co-sub {
	font-size: 12px;
	color: #5f6b7a;
}
.co-split {
	margin-bottom: 14px;
}
.co-title {
	max-width: 380px;
	white-space: nowrap;
	overflow: hidden;
	text-overflow: ellipsis;
}
</style>
